import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

import API_BASE from '../../utils/api';
const API = API_BASE;

const formatCurrency = (amount) =>
  `Rs. ${Number(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getOutstandingAmount = (fee) =>
  Math.max(Number(fee.amount || 0) - Number(fee.paidAmount || 0), 0);

const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getDownloadFileName = (headers, fallbackName) => {
  const disposition = headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallbackName;
};

const ParentFees = () => {
  const { user } = useAuth();
  const [fees, setFees] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [payingFee, setPayingFee] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [receiptDownloadId, setReceiptDownloadId] = useState('');
  const [latestReceipt, setLatestReceipt] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
  });

  const [paymentMethod, setPaymentMethod] = useState('phonepe');
  const [upiId, setUpiId] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');

  useEffect(() => {
    if (!user?.studentId) {
      setLoading(false);
      return;
    }

    fetchFees();
    fetchNotifications();
  }, [user]);

  const fetchFees = async () => {
    try {
      const response = await axios.get(`${API}/fees/student/${user.studentId}`);
      const feeList = Array.isArray(response.data) ? response.data : [];
      setFees(feeList);

      const total = feeList.reduce((sum, fee) => sum + Number(fee.amount || 0), 0);
      const paid = feeList.reduce((sum, fee) => sum + Number(fee.paidAmount || 0), 0);
      const pending = feeList.reduce((sum, fee) => sum + getOutstandingAmount(fee), 0);
      const overdue = feeList
        .filter((fee) => fee.status === 'overdue')
        .reduce((sum, fee) => sum + getOutstandingAmount(fee), 0);

      setStats({ total, paid, pending, overdue });
    } catch (error) {
      console.error('Error fetching fees:', error);
      setPaymentError('Unable to load fee records right now.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?unreadOnly=true`);
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications((current) => current.filter((item) => item._id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const resetPaymentForm = () => {
    setPaymentMethod('phonepe');
    setUpiId('');
    setCardNumber('');
    setCardName('');
    setCardExpiry('');
    setCardCvv('');
    setPaymentError('');
  };

  const openPaymentModal = (fee) => {
    setPayingFee(fee);
    resetPaymentForm();
  };

  const closePaymentModal = () => {
    setPayingFee(null);
    resetPaymentForm();
  };

  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length < 3) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const downloadReceipt = async (feeId) => {
    try {
      setReceiptDownloadId(feeId);
      const response = await axios.get(`${API}/fees/${feeId}/receipt/download`, {
        responseType: 'blob',
      });

      const fileName = getDownloadFileName(response.headers, `receipt-${feeId}.html`);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      setPaymentError(error.response?.data?.error || 'Unable to download receipt.');
    } finally {
      setReceiptDownloadId('');
    }
  };

  const handlePayment = async (event) => {
    event.preventDefault();
    setPaymentError('');

    if ((paymentMethod === 'phonepe' || paymentMethod === 'upi') && !upiId.trim()) {
      setPaymentError('Please enter your UPI ID.');
      return;
    }

    if (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') {
      const normalizedCardNumber = cardNumber.replace(/\s/g, '');
      if (normalizedCardNumber.length < 16) {
        setPaymentError('Please enter a valid 16-digit card number.');
        return;
      }
      if (!cardName.trim()) {
        setPaymentError('Please enter the cardholder name.');
        return;
      }
      if (cardExpiry.length < 5) {
        setPaymentError('Please enter the card expiry in MM/YY format.');
        return;
      }
      if (cardCvv.length < 3) {
        setPaymentError('Please enter a valid CVV.');
        return;
      }
    }

    try {
      setProcessing(true);
      const response = await axios.post(`${API}/fees/${payingFee._id}/pay-online`, {
        paymentMethod,
        upiId,
        cardNumber: cardNumber.replace(/\s/g, ''),
        cardExpiry,
        cardCvv,
        cardName,
      });

      const receiptInfo = {
        feeId: payingFee._id,
        receiptNumber: response.data.receiptNumber,
      };

      setLatestReceipt(receiptInfo);
      setPaymentSuccess(
        `Payment completed for ${formatCurrency(payingFee.amount)}. Receipt ${response.data.receiptNumber} is ready to download.`
      );
      closePaymentModal();
      await fetchFees();
      await fetchNotifications();
    } catch (error) {
      console.error('Payment failed:', error);
      setPaymentError(error.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const feeReminders = notifications.filter(
    (item) => item.type === 'fee_reminder' || item.type === 'payment'
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Fees</h1>
              <p className="text-sm text-gray-500">
                Pay dues online and download receipts any time.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowNotifications((current) => !current)}
                  className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {notifications.length > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                      {notifications.length}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 mt-2 max-h-96 w-80 overflow-y-auto rounded-lg border bg-white shadow-xl">
                    <div className="border-b px-4 py-3 font-semibold text-gray-700">Notifications</div>
                    {feeReminders.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-gray-500">No new fee notifications.</div>
                    ) : (
                      feeReminders.map((notification) => (
                        <button
                          key={notification._id}
                          type="button"
                          onClick={() => markNotificationRead(notification._id)}
                          className="block w-full border-b px-4 py-3 text-left hover:bg-gray-50"
                        >
                          <div className="text-sm font-medium text-gray-800">{notification.title}</div>
                          <div className="mt-1 text-xs text-gray-500">{notification.message}</div>
                          <div className="mt-1 text-xs text-gray-400">
                            {new Date(notification.createdAt).toLocaleString()}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        <div className="p-6">
          {paymentSuccess && (
            <div className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4 text-green-800">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <span>{paymentSuccess}</span>
                {latestReceipt && (
                  <button
                    type="button"
                    onClick={() => downloadReceipt(latestReceipt.feeId)}
                    className="rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
                  >
                    Download Latest Receipt
                  </button>
                )}
              </div>
            </div>
          )}

          {paymentError && (
            <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
              {paymentError}
            </div>
          )}

          {feeReminders.some((item) => item.type === 'fee_reminder') && (
            <div className="mb-6 rounded-lg border border-orange-300 bg-orange-50 p-4 text-orange-800">
              Pending fee reminders are available in your notifications. You can pay outstanding dues and download the receipt immediately after payment.
            </div>
          )}

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="text-sm text-gray-500">Total Fees</div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{formatCurrency(stats.total)}</div>
            </div>
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="text-sm text-gray-500">Paid So Far</div>
              <div className="mt-2 text-2xl font-bold text-green-700">{formatCurrency(stats.paid)}</div>
            </div>
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="text-sm text-gray-500">Outstanding</div>
              <div className="mt-2 text-2xl font-bold text-amber-700">{formatCurrency(stats.pending)}</div>
            </div>
            <div className="rounded-lg bg-white p-6 shadow">
              <div className="text-sm text-gray-500">Overdue</div>
              <div className="mt-2 text-2xl font-bold text-red-700">{formatCurrency(stats.overdue)}</div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-800">Fee Records</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Fee Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Paid</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Receipt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {fees.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                        No fee records found.
                      </td>
                    </tr>
                  ) : (
                    fees.map((fee) => {
                      const outstandingAmount = getOutstandingAmount(fee);
                      const hasReceipt = Number(fee.paidAmount || 0) > 0;

                      return (
                        <tr key={fee._id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="font-medium capitalize text-gray-900">{fee.feeType}</div>
                            <div className="text-xs text-gray-500">{fee.paymentMode ? `Paid via ${fee.paymentMode}` : 'Not paid yet'}</div>
                          </td>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">{formatCurrency(fee.amount)}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">{formatCurrency(fee.paidAmount)}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">{formatCurrency(outstandingAmount)}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {fee.dueDate ? new Date(fee.dueDate).toLocaleDateString() : '-'}
                            <div className="mt-1">
                              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(fee.status)}`}>
                                {fee.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {hasReceipt ? (
                              <div>
                                <div className="font-medium text-gray-900">{fee.receiptNumber || 'Latest receipt available'}</div>
                                <div className="mt-2">
                                  <button
                                    type="button"
                                    onClick={() => downloadReceipt(fee._id)}
                                    disabled={receiptDownloadId === fee._id}
                                    className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                                  >
                                    {receiptDownloadId === fee._id ? 'Downloading...' : 'Download Receipt'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">Receipt will appear after payment.</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {outstandingAmount > 0 ? (
                              <button
                                type="button"
                                onClick={() => openPaymentModal(fee)}
                                className="rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700"
                              >
                                {Number(fee.paidAmount || 0) > 0 ? 'Pay Balance' : 'Pay Now'}
                              </button>
                            ) : (
                              <span className="font-medium text-green-700">Fully Paid</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {payingFee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="rounded-t-2xl bg-gradient-to-r from-indigo-700 to-cyan-700 p-6 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold">Fee Payment</h2>
                  <p className="mt-1 text-sm text-indigo-100">Receipt will be generated automatically after payment.</p>
                </div>
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="text-2xl leading-none text-white hover:text-indigo-100"
                >
                  x
                </button>
              </div>
              <div className="mt-4 rounded-xl bg-white/10 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-indigo-100">Fee Type</div>
                    <div className="mt-1 text-lg font-semibold capitalize">{payingFee.feeType}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wide text-indigo-100">Paying Now</div>
                    <div className="mt-1 text-2xl font-bold">{formatCurrency(getOutstandingAmount(payingFee))}</div>
                  </div>
                </div>
                <div className="mt-2 text-sm text-indigo-100">
                  Due on {new Date(payingFee.dueDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 grid grid-cols-2 gap-3">
                {[
                  { value: 'phonepe', label: 'PhonePe' },
                  { value: 'upi', label: 'UPI' },
                  { value: 'credit_card', label: 'Credit Card' },
                  { value: 'debit_card', label: 'Debit Card' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPaymentMethod(option.value)}
                    className={`rounded-xl border-2 px-4 py-3 text-sm font-medium transition ${
                      paymentMethod === option.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handlePayment} className="space-y-4">
                {paymentError && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {paymentError}
                  </div>
                )}

                {(paymentMethod === 'phonepe' || paymentMethod === 'upi') && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">UPI ID</label>
                    <input
                      type="text"
                      value={upiId}
                      onChange={(event) => setUpiId(event.target.value)}
                      placeholder="name@ybl or mobile@upi"
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                )}

                {(paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Card Number</label>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(event) => setCardNumber(formatCardNumber(event.target.value))}
                        placeholder="1234 5678 9012 3456"
                        maxLength="19"
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Cardholder Name</label>
                      <input
                        type="text"
                        value={cardName}
                        onChange={(event) => setCardName(event.target.value.toUpperCase())}
                        placeholder="NAME ON CARD"
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Expiry</label>
                        <input
                          type="text"
                          value={cardExpiry}
                          onChange={(event) => setCardExpiry(formatExpiry(event.target.value))}
                          placeholder="MM/YY"
                          maxLength="5"
                          className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">CVV</label>
                        <input
                          type="password"
                          value={cardCvv}
                          onChange={(event) =>
                            setCardCvv(event.target.value.replace(/\D/g, '').slice(0, 4))
                          }
                          placeholder="123"
                          maxLength="4"
                          className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-indigo-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={processing}
                  className="w-full rounded-xl bg-indigo-700 px-4 py-4 text-lg font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {processing ? 'Processing...' : `Pay ${formatCurrency(getOutstandingAmount(payingFee))}`}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentFees;
